/// Dispatches server-pushed audio WS messages into the SoundtrackEngine.
///
/// The server's per-session SoundtrackManager is the single source of
/// truth for which cue plays when. It sends one of three message types
/// on either the game-server stream (`soundtrack_play` / `soundtrack_stop`
/// / `fx_play`) or the lobby-server stream (`lobby:soundtrack-play` /
/// `lobby:soundtrack-stop` / `lobby:fx-play`). This module catches them
/// from the store handlers and routes to the engine.
///
/// Fades are NOT taken from these messages. The payloads still carry fade
/// fields (`fade_ms` / `fade_in_ms` / `fade_out_ms`), but we intentionally
/// ignore them and let the engine apply its own two client-side defaults
/// (soundtrack vs cue — see soundtrack-engine.ts). The server fields are
/// reserved for possible future per-cue fade control.

import type { ServerMessage } from '@danda/shared/types/websocket';
import type { LobbyEvent } from '@danda/shared/types/lobby';
import { getSoundtrackEngine } from './soundtrack-engine';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { websocketService } from '@/lib/websocket-service';

/// Called once at app mount. Two responsibilities:
///   1. Ensure the singleton engine exists so localStorage settings
///      are read before the first WS message arrives.
///   2. Subscribe to lobby-store `activeChatRoomId` changes and report
///      them to the server via `lobby:set-chat-focus`. Server picks
///      the cue from the focused tab + active NPC; client never
///      triggers audio playback by itself.
export function initAudioRouting(): void {
  getSoundtrackEngine();
  startChatFocusReporter();
}

/// Web chatRoom.type → server (tab, active_npc) shape. Mirrors the
/// resolver in `backend/lobby-server/src/lobby/soundtrack.rs::cue_id_for_chat_focus`.
function chatRoomToFocus(roomType: string | undefined): { tab: string; activeNpc: string | null } {
  switch (roomType) {
    case 'party':
      return { tab: 'party', activeNpc: null };
    case 'tavian':
      return { tab: 'tavian', activeNpc: 'tavian' };
    case 'inner':
      return { tab: 'inner_voice', activeNpc: null };
    case 'kira':
      return { tab: 'tavian', activeNpc: 'kira' };
    case 'aldric':
      return { tab: 'tavian', activeNpc: 'aldric' };
    case 'grolt':
      return { tab: 'tavian', activeNpc: 'grolt' };
    case 'private':
    case undefined:
    case null:
    default:
      return { tab: 'none', activeNpc: null };
  }
}

let _chatFocusUnsub: (() => void) | null = null;
let _lastFocusKey: string | null = null;

function startChatFocusReporter(): void {
  if (_chatFocusUnsub) return; // already started

  function report(state: ReturnType<typeof useLobbyStore.getState>): void {
    const room = state.chatRooms.find((r) => r.id === state.activeChatRoomId);
    const focus = chatRoomToFocus(room?.type);
    const key = `${focus.tab}|${focus.activeNpc ?? ''}`;
    if (key === _lastFocusKey) return;
    _lastFocusKey = key;
    try {
      websocketService.setChatFocus(focus.tab, focus.activeNpc ?? undefined);
    } catch (e) {
      console.warn('[audio-routing] setChatFocus failed', e);
    }
  }

  // Fire once for the initial state, then subscribe for changes. The
  // store will not have its real data until lobby-state lands, but
  // sending `none` (no room) is harmless — the server resolves to
  // lobby.main which is already playing.
  report(useLobbyStore.getState());
  _chatFocusUnsub = useLobbyStore.subscribe((s) => report(s));
}

/// Handle a game-server `ServerMessage`. Returns true if the message
/// was an audio message and was handled; false otherwise. Stores call
/// this on every dispatch and only fall through to their own logic if
/// it returns false.
export function handleGameAudioMessage(msg: ServerMessage): boolean {
  switch (msg.type) {
    case 'soundtrack_play': {
      const engine = getSoundtrackEngine();
      engine.playSoundtrack(msg.url, { loop: msg.loop });
      return true;
    }
    case 'soundtrack_stop': {
      const engine = getSoundtrackEngine();
      engine.stopSoundtrack();
      return true;
    }
    case 'fx_play': {
      const engine = getSoundtrackEngine();
      engine.playFx(msg.url);
      return true;
    }
    default:
      return false;
  }
}

/// Handle a lobby `LobbyEvent`. Same contract as `handleGameAudioMessage`.
export function handleLobbyAudioMessage(msg: LobbyEvent): boolean {
  switch (msg.type) {
    case 'lobby:soundtrack-play': {
      const engine = getSoundtrackEngine();
      engine.playSoundtrack(msg.url, { loop: msg.loop });
      return true;
    }
    case 'lobby:soundtrack-stop': {
      const engine = getSoundtrackEngine();
      engine.stopSoundtrack();
      return true;
    }
    case 'lobby:fx-play': {
      const engine = getSoundtrackEngine();
      engine.playFx(msg.url);
      return true;
    }
    default:
      return false;
  }
}
